import { Fragment, memo, useEffect, useMemo, useRef } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { LargeProjectError } from './LargeProjectError';
import { SupabaseStopgapButton } from './supabase/SupabaseStopgapButton';
import { Markdown } from '~/components/chat/Markdown';
import { UserMessage } from '~/components/chat/UserMessage';
import { AssistantMessage } from '~/components/chat/assistant-message';
import { PotentialProblems } from '~/components/chat/problems/PotentialProblems';
import { SupabaseMigration } from '~/components/chat/supabase/SupabaseMigration';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { OutOfTokensAlert } from '~/components/ui/pricing/OutOfTokensAlert';
import { MessageProvider } from '~/lib/context/message';
import type { ChatMessage, Message } from '~/types/chat';
import { classNames } from '~/utils/classNames';
import { isThinkingAnnotation } from '~/utils/thinking';

export interface MessageProps {
  message: Message;
  isLast: boolean;
  isLastChatMessage: boolean;
  isLastUserMessage: boolean;
  isStreaming: boolean;
  disableQuickActions?: boolean;
  setPinnedMessageHeight: (height: number) => void;
  scrollToBottom: ScrollToBottom;
  messageBoxHeight: number;
  acceptSupabaseChange: (changeId: string) => Promise<void>;
  discardSupabaseChange: (changeId: string, removeMessage?: boolean) => Promise<void>;
  resubmit?: (message: ChatMessage) => void;
  onRollback?: (message: Message) => void;
  consecutiveProblemCount: number;
}

export const MessageItem = memo((props: MessageProps) => {
  const {
    message,
    isLast,
    isLastChatMessage,
    isLastUserMessage,
    isStreaming,
    disableQuickActions,
    setPinnedMessageHeight,
    scrollToBottom,
    messageBoxHeight,
    acceptSupabaseChange,
    discardSupabaseChange,
    resubmit,
    onRollback,
    consecutiveProblemCount,
  } = props;

  const { role } = message;
  const isUserMessage = role === 'user';
  const container = useRef<HTMLDivElement>(null);

  const attachments = useMemo(() => {
    return message.experimental_attachments?.map((attachment) => {
      return {
        url: `${location.origin}/api/upload/${encodeURIComponent(attachment.url)}`,
        name: attachment.name,
        contentType: attachment.contentType,
      };
    });
  }, [message.experimental_attachments]);

  const isLargeProjectError = message.error?.code === 'project-too-big';

  const shouldDisplayOutOfTokensAlert = message.error?.code === 'rate-limited';

  const shouldShowError =
    !isLargeProjectError &&
    !shouldDisplayOutOfTokensAlert &&
    (isUserMessage ? message.status === 'unsent' : message.status === 'incomplete');

  const isRetryable = !isStreaming && (message.error?.isRetryable ?? shouldShowError) && isLastChatMessage;

  useEffect(() => {
    if (isLastUserMessage && container.current) {
      document.documentElement.style.setProperty(
        '--computed-last-user-message-height',
        `${container.current.offsetHeight}px`,
      );
    }
  }, [isLastUserMessage]);

  if (message.type === 'problems') {
    return (
      <PotentialProblems
        key={message.id}
        isLast={isLast}
        consecutiveProblemCount={consecutiveProblemCount}
        resolved={message.resolved}
        problems={message.problems}
        onStickyHeight={setPinnedMessageHeight}
        scrollToBottom={scrollToBottom}
        messageBoxHeight={messageBoxHeight}
      />
    );
  }

  if (message.type === 'supabase') {
    const { change, changeStatus } = message;

    switch (change.type) {
      case 'migration': {
        return (
          <SupabaseMigration
            key={message.id}
            isLast={isLast}
            messageBoxHeight={messageBoxHeight}
            change={change}
            changeStatus={changeStatus}
            onStickyHeight={setPinnedMessageHeight}
            scrollToBottom={scrollToBottom}
            acceptSupabaseChange={acceptSupabaseChange}
          />
        );
      }
      case 'connect-required': {
        return (
          <SupabaseStopgapButton
            key={message.id}
            isLast={isLast}
            messageBoxHeight={messageBoxHeight}
            change={change}
            onStickyHeight={setPinnedMessageHeight}
            scrollToBottom={scrollToBottom}
            discardSupabaseChange={discardSupabaseChange}
          />
        );
      }
      case 'migration-error':
      default: {
        return (
          <PotentialProblems
            key={message.id}
            isLast={isLast}
            consecutiveProblemCount={change.problems.length}
            messageBoxHeight={messageBoxHeight}
            resolved={message.changeStatus !== 'pending'}
            problems={change.problems}
            onStickyHeight={setPinnedMessageHeight}
            scrollToBottom={scrollToBottom}
            customResolve={() => acceptSupabaseChange(change.id)}
            customClear={() => discardSupabaseChange(change.id, true)}
          />
        );
      }
    }
  }

  const containsThinking =
    message.role === 'assistant' &&
    (message.parts.some((part) => part.type === 'reasoning' && part.reasoning) ||
      message.annotations?.some((annotation) => isThinkingAnnotation(annotation)));

  const shouldRenderMessage = message.role === 'assistant' || message.content || containsThinking || shouldShowError;

  if (!shouldRenderMessage) {
    return null;
  }

  return (
    <Fragment key={message.id}>
      <div
        className={classNames('relative flex flex-col overflow-hidden mx-[var(--chat-padding)]', {
          'bg-bolt-elements-messages-background px-4 py-3 rounded-lg self-end': isUserMessage,
        })}
        data-message-id={message.id}
        ref={container}
      >
        <div className="grid grid-col-1 w-full">
          <MessageProvider value={message}>
            {isUserMessage ? (
              <UserMessage
                renderHtml={message.renderHtml}
                attachments={attachments}
                inspectedElement={message.inspectedElement}
                fileSelections={message.fileSelections}
                content={message.input ?? message.content}
              />
            ) : (
              <AssistantMessage
                message={message}
                disableQuickActions={disableQuickActions}
                scrollToBottom={scrollToBottom}
                useDynamicHeight={isLast && !isLargeProjectError}
              />
            )}
          </MessageProvider>
        </div>

        {shouldShowError && (
          <div className="pt-4">
            <Alert
              title={message.error?.title ?? 'Error'}
              type="warning"
              direction="row"
              actions={
                <>
                  {isRetryable && resubmit && (
                    <Button variant="tint" size="sm" color="warning" onClick={() => resubmit(message)}>
                      Retry
                    </Button>
                  )}
                </>
              }
            >
              <Markdown className="text-bolt-ds-textSecondary" html>
                {message.error?.message ??
                  (message.status === 'unsent'
                    ? 'This message was not sent.'
                    : `This message didn't complete its response.`)}
              </Markdown>
            </Alert>
          </div>
        )}
      </div>

      {shouldDisplayOutOfTokensAlert && <OutOfTokensAlert className="mx-4" />}

      {isLargeProjectError && (
        <LargeProjectError
          className="mx-[var(--chat-padding)]"
          isLast={isLast}
          messageId={message.messageId || message.id}
          onUndo={() => onRollback?.(message)}
        />
      )}
    </Fragment>
  );
});
